import Router from 'next/router';

import { create } from 'zustand';

import { lockScroll, unlockScroll } from '@utils/misc';

export type OverlayOption = {
  name: string;
  onClose: () => void;
  routeChange?: boolean;
};

export type OverlayPopOption = {
  afterClose?: () => void;
};

export type OverlayState = {
  isLocked: boolean;
  stack: OverlayOption[];
  open: (options: OverlayOption) => void;
  close: (options?: OverlayPopOption) => void;
  pop: (options?: OverlayPopOption) => void;
};

const _handleOpitons = (options?: OverlayPopOption) => {
  if (!options) {
    return;
  }

  if (options.afterClose) {
    setTimeout(options.afterClose, 400);
  }
};

export const useOverlayStore = create<OverlayState>((set, get) => ({
  stack: [],
  isLocked: false,
  open: (options) => {
    const { isLocked } = get();

    if (!isLocked) {
      lockScroll();
      set({ isLocked: true });
    }

    if (options.routeChange) {
      const url = new URL(location.href);

      if (Router.router) {
        Router.router.isFirstPopStateEvent = true;
      }
      url.hash = options.name;
      history.pushState(history.state, '', url);
    }

    set((state) => ({
      stack: state.stack.concat(options),
    }));
  },
  close: (options) => {
    const { stack, pop } = get();

    if (stack.length === 0) {
      return;
    }

    const last = stack[stack.length - 1];
    if (last.routeChange) {
      history.back();
      _handleOpitons(options);
      return;
    }

    pop(options);
  },
  pop: (options) => {
    const { stack } = get();
    if (stack.length === 0) {
      return;
    }

    const popped = stack[stack.length - 1];
    popped.onClose();

    if (stack.length === 1) {
      unlockScroll();
      set({ isLocked: false });
    }

    _handleOpitons(options);

    set((state) => ({ stack: state.stack.slice(0, -1) }));
  },
}));

export const useCloseOverlay = () => useOverlayStore((state) => state.close);
